import { useCallback, useEffect } from 'react';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import logs from '@atlassian/jira-common-util-logging/src/log';
import { type BaseUrl, toBaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import { getApplicationProperty, setApplicationProperties } from './services/rest-v3/index.tsx';
import type { ReturnObject } from './types';

const initialState = { loading: true } as const;

const fetchApplicationProperty = <T extends boolean = boolean>(
	baseUrl: BaseUrl,
	propertyKey: string,
	parseReturnValue: (value: string) => T,
) =>
	getApplicationProperty(baseUrl, propertyKey)
		// @ts-expect-error - TS2345 - Argument of type '({ value }: { value: any; }) => boolean' is not assignable to parameter of type '(value: unknown) => boolean | PromiseLike<boolean>'.
		.then(({ value }) => parseReturnValue(value))
		.catch((error) => {
			logs.safeErrorWithoutCustomerData(
				`${propertyKey}.fetch`,
				'Error reading application property',
				error,
			);

			return undefined;
		});

const setApplicationProperty = <T = boolean,>(
	baseUrl: BaseUrl,
	newValue: string,
	propertyKey: string,
	parseReturnValue: (value: string) => T,
) => {
	const data = {
		id: propertyKey,
		value: newValue,
	};

	return (
		setApplicationProperties(baseUrl, propertyKey, JSON.stringify(data))
			// @ts-expect-error - TS2345 - Argument of type '({ value }: { value: any; }) => boolean' is not assignable to parameter of type '(value: unknown) => boolean | PromiseLike<boolean>'.
			.then(({ value }) => parseReturnValue(value))
			.catch((error) => {
				logs.safeErrorWithoutCustomerData(
					`${propertyKey}.set`,
					'Failed to update application property',
					error,
				);

				return undefined;
			})
	);
};

export const useApplicationProperty = <T = boolean,>(
	propertyKey: string,
	parseReturnValue: (value: string) => T,
): ReturnObject<T> => {
	const baseUrl = toBaseUrl('');

	const fetchRequest = useCallback(
		// @ts-expect-error Argument of type '(value: string) => T' is not assignable to parameter of type '(value: string) => boolean'. Type 'T' is not assignable to type 'boolean'.ts(2345)
		() => fetchApplicationProperty(baseUrl, propertyKey, parseReturnValue),
		[baseUrl, parseReturnValue, propertyKey],
	);
	const setRequest = useCallback(
		(value: string) => setApplicationProperty(baseUrl, value, propertyKey, parseReturnValue),
		[baseUrl, parseReturnValue, propertyKey],
	);

	const {
		fetch,
		data: fetchData,
		error: fetchError,
		loading: fetchLoading,
	} = useService(fetchRequest, initialState);

	const { data, error, loading, fetch: update } = useService(setRequest);

	useEffect(() => {
		fetch();
	}, [fetch]);

	// Value from `setApplicationProperties()` takes precedence
	const value = data !== undefined ? data : fetchData;

	const actions = {
		updatePropertyValue: update,
	};

	return [
		{
			// @ts-expect-error - TS2322 - Type 'unknown' is not assignable to type 'boolean | undefined'.
			value,
			fetchError,
			updateError: error,
			loading: loading || fetchLoading,
		},
		// @ts-expect-error - TS2322 - Type '{ updatePropertyValue: (requestData: string) => Promise<unknown>; }' is not assignable to type 'Actions'.
		actions,
	];
};
